if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Initial filtering by color
        const productInfo = this.closest('product-info');
        if (productInfo && typeof productInfo.getSelectedColorData === 'function') {
          const colorData = productInfo.getSelectedColorData();
          if (colorData) {
            this.filterMediaByColor(colorData.selectedColor, colorData.allColors);
          }
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Do not prepend elements as it breaks layout smoothness and causes blinking/jumping.
        // The elements are already sorted by color in filterMediaByColor.

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      filterMediaByColor(selectedColor, allColors) {
        if (!selectedColor) return;

        const slides = this.elements.viewer ? this.elements.viewer.querySelectorAll('[id^="Slide-"]') : [];
        const thumbnails = this.elements.thumbnails ? this.elements.thumbnails.querySelectorAll('[id^="Slide-Thumbnails-"]') : [];

        const productInfo = this.closest('product-info');
        const productModal = productInfo ? productInfo.productModal : null;
        const modalMediaItems = productModal ? productModal.querySelectorAll('[data-media-alt]') : [];

        // 1. Sort slides
        const list = this.elements.viewer ? this.elements.viewer.querySelector('ul') : null;
        if (list && slides.length > 0) {
          const selectedItems = [];
          const otherItems = [];

          slides.forEach((slide) => {
            const altText = (slide.dataset.mediaAlt || '').toLowerCase();
            let isSelectedColor = false;
            
            if (altText.includes(selectedColor)) {
              isSelectedColor = true;
            } else {
              const hasOtherColor = allColors.some(color => color !== selectedColor && altText.includes(color));
              if (!hasOtherColor) {
                isSelectedColor = true;
              }
            }

            slide.style.display = '';
            slide.classList.remove('hidden');

            if (isSelectedColor) {
              selectedItems.push(slide);
            } else {
              otherItems.push(slide);
            }
          });

          const sortedItems = [...selectedItems, ...otherItems];
          let needsReorder = false;
          for (let i = 0; i < slides.length; i++) {
            if (slides[i] !== sortedItems[i]) {
              needsReorder = true;
              break;
            }
          }

          if (needsReorder) {
            const fragment = document.createDocumentFragment();
            sortedItems.forEach((item) => fragment.appendChild(item));
            list.appendChild(fragment);
          }
        }

        // 2. Sort thumbnails
        const thumbnailList = this.elements.thumbnails ? this.elements.thumbnails.querySelector('ul') : null;
        if (thumbnailList && thumbnails.length > 0) {
          const selectedThumbs = [];
          const otherThumbs = [];

          thumbnails.forEach((thumbnail) => {
            const altText = (thumbnail.dataset.mediaAlt || '').toLowerCase();
            let isSelectedColor = false;
            
            if (altText.includes(selectedColor)) {
              isSelectedColor = true;
            } else {
              const hasOtherColor = allColors.some(color => color !== selectedColor && altText.includes(color));
              if (!hasOtherColor) {
                isSelectedColor = true;
              }
            }

            thumbnail.style.display = '';
            thumbnail.classList.remove('hidden');

            if (isSelectedColor) {
              selectedThumbs.push(thumbnail);
            } else {
              otherThumbs.push(thumbnail);
            }
          });

          const sortedThumbs = [...selectedThumbs, ...otherThumbs];
          let thumbsNeedReorder = false;
          for (let i = 0; i < thumbnails.length; i++) {
            if (thumbnails[i] !== sortedThumbs[i]) {
              thumbsNeedReorder = true;
              break;
            }
          }

          if (thumbsNeedReorder) {
            const fragment = document.createDocumentFragment();
            sortedThumbs.forEach((item) => fragment.appendChild(item));
            thumbnailList.appendChild(fragment);
          }
        }

        // 3. Sort modal items
        const modalList = productModal ? productModal.querySelector('.product-media-modal__content') : null;
        if (modalList && modalMediaItems.length > 0) {
          const selectedModalItems = [];
          const otherModalItems = [];

          modalMediaItems.forEach((item) => {
            const altText = (item.getAttribute('data-media-alt') || '').toLowerCase();
            let isSelectedColor = false;

            if (altText.includes(selectedColor)) {
              isSelectedColor = true;
            } else {
              const hasOtherColor = allColors.some(color => color !== selectedColor && altText.includes(color));
              if (!hasOtherColor) {
                isSelectedColor = true;
              }
            }

            item.style.display = '';
            item.classList.remove('hidden');

            if (isSelectedColor) {
              selectedModalItems.push(item);
            } else {
              otherModalItems.push(item);
            }
          });

          const sortedModalItems = [...selectedModalItems, ...otherModalItems];
          let modalNeedReorder = false;
          for (let i = 0; i < modalMediaItems.length; i++) {
            if (modalMediaItems[i] !== sortedModalItems[i]) {
              modalNeedReorder = true;
              break;
            }
          }

          if (modalNeedReorder) {
            const fragment = document.createDocumentFragment();
            sortedModalItems.forEach((item) => fragment.appendChild(item));
            modalList.appendChild(fragment);
          }
        }

        const activeSlide = this.elements.viewer ? this.elements.viewer.querySelector('.is-active') : null;
        if (this.elements.viewer && (!activeSlide || activeSlide.classList.contains('hidden') || activeSlide.style.display === 'none')) {
          const firstVisibleSlide = Array.from(slides).find(slide => !slide.classList.contains('hidden'));
          if (firstVisibleSlide) {
            this.setActiveMedia(firstVisibleSlide.dataset.mediaId, false);
          }
        }

        const sliders = this.querySelectorAll('slider-component');
        sliders.forEach((slider) => {
          if (slider.resetPages) {
            slider.resetPages();
          }
        });
      }
    }
  );
}
